import React, { useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import * as Popover from "@radix-ui/react-popover";
import { TimeFormatOption } from "../../../types/account/enum";
import { useDateTimeHandler } from "../../../hooks/block/dateTime/useDateTimeHandler";
import formatTime from "../../../utils/common/dateTime/formatTime";
import styles from "./DateModal.module.css";
import { useDateModalContext } from "./DateModalContext";

const StartTimePicker: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { dateTimeStart, isTimeStartOpen, setIsTimeStartOpen, onTimeStartChange, timeFormat } =
    useDateModalContext();

  const {
    handleKeyDownCapture,
    handleClick,
    handleMouseDown,
    handleWrapperKeyDown,
    handleWrapperClick,
  } = useDateTimeHandler({
    isOpen: isTimeStartOpen,
    onOpenChange: setIsTimeStartOpen,
    selectedValue: dateTimeStart,
    onChange: onTimeStartChange,
  });

  useEffect(() => {
    if (!isTimeStartOpen) return;
    const handleOutsideInteract = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // TaskDetail 핸들러 차단
        setIsTimeStartOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setIsTimeStartOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideInteract, true);
    document.addEventListener("focusin", handleOutsideInteract, true);
    document.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });

    return () => {
      document.removeEventListener("pointerdown", handleOutsideInteract, true);
      document.removeEventListener("focusin", handleOutsideInteract, true);
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isTimeStartOpen]);

  return (
    <Popover.Root
      open={isTimeStartOpen}
      onOpenChange={setIsTimeStartOpen}
      modal={true}
    >
      <Popover.Trigger asChild>
        <div
          onClick={(event) => {
            event.preventDefault();
            setIsTimeStartOpen(!isTimeStartOpen);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${styles["date__start-time"]} data-[state=open]:rounded-[5px] data-[state=open]:bg-w-8-hover`}
        >
          <span
            className={`${styles["setting-property-label"]} ${dateTimeStart && styles["label-on"]}`}
          >
            {dateTimeStart ? formatTime(dateTimeStart, timeFormat) : "Time"}
          </span>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={containerRef}
          className={`${styles["datepickerTimeWrap"]} ${"datepickerTimeWrap"}`}
          side="bottom"
          align="start"
          sideOffset={5}
          tabIndex={-1}
          onKeyDownCapture={handleKeyDownCapture}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
        >
          <div
            onKeyDown={handleWrapperKeyDown}
            onClick={handleWrapperClick}
          >
            <DatePicker
              selected={dateTimeStart}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              inline
              timeFormat={timeFormat === TimeFormatOption.HOUR24 ? "HH:mm" : "h:mm aaa"}
              onChange={onTimeStartChange}
              onSelect={(date) => {
                onTimeStartChange(date);
                setIsTimeStartOpen(false);
              }}
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default StartTimePicker;
